import React from 'react';
import { getCategoryLabel } from '../documentFilters';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import DefinitionGrid, { type DefinitionGridItem } from '../../common/DefinitionGrid';
import { DATE_TIME_FORMAT, formatDateSafe, formatFileSize, getBestDate } from '@utils/dateUtils';
import type { DocumentDetailModel } from '@hooks/useDocumentDetail';

interface DocumentMetadataFieldsProps {
  doc: DocumentDetailModel;
}

/** MUI appended the extension unless the title already carried it. */
const displayFileName = (doc: DocumentDetailModel) =>
  doc.title?.toLowerCase().endsWith(`.${doc.fileType?.toLowerCase()}`)
    ? doc.title
    : `${doc.title}.${doc.fileType}`;

const DocumentMetadataFields: React.FC<DocumentMetadataFieldsProps> = ({ doc }) => {
  const { t, i18n } = useTranslation();

  const items: DefinitionGridItem[] = [
    { label: `${t('documents.fileName')}:`, value: displayFileName(doc) },
    { label: `${t('documents.fileType')}:`, value: doc.fileType.toUpperCase() },
    { label: `${t('documents.fileSize')}:`, value: formatFileSize(doc.size || doc.fileSize) },
    { label: `${t('documents.category')}:`, value: getCategoryLabel(doc) },
    { label: `${t('documents.department')}:`, value: doc.metadata.department },
    { label: `${t('documents.classification')}:`, value: doc.metadata.classification },
    {
      label: `${t('documents.status')}:`,
      value: (
        <Badge variant="success" className="revy-cjk w-fit px-2 text-sm font-normal">
          {doc.status}
        </Badge>
      ),
    },
    {
      label: `${t('documents.createdAt')}:`,
      value: formatDateSafe(getBestDate(doc), DATE_TIME_FORMAT, { t, language: i18n.language }),
    },
    {
      label: `${t('documents.lastModified')}:`,
      value: formatDateSafe(doc.metadata?.lastModified || getBestDate(doc), DATE_TIME_FORMAT, {
        t,
        language: i18n.language,
      }),
    },
    { label: `${t('documents.retentionPeriod')}:`, value: doc.metadata.retentionPeriod },
  ];

  return <DefinitionGrid items={items} labelWidth="200px" className="gap-4" />;
};

export default DocumentMetadataFields;
